import type { EnumTypeDefinition } from "@superego/schema";
import { type Control, useController } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import classnames from "../../../utils/classnames.js";
import {
  FieldError,
  Input,
  Select,
  SelectButton,
  SelectOptions,
  TextField,
} from "../../design-system/forms/forms.js";
import AnyFieldLabel from "./AnyFieldLabel.js";
import * as cs from "./RHFContentField.css.js";
import { useUiOptions } from "./uiOptions.js";

interface Props {
  typeDefinition: EnumTypeDefinition;
  isNullable: boolean;
  isListItem: boolean;
  control: Control;
  name: string;
  label: string;
  autoFocus: boolean;
}
export default function EnumField({
  typeDefinition,
  isNullable,
  isListItem,
  control,
  name,
  label,
  autoFocus,
}: Props) {
  const { zoomLevel, isReadOnly } = useUiOptions();
  const {
    field: { ref: fieldRef, ...field },
    fieldState,
  } = useController({ control, name });
  const sortedMemberNames =
    typeDefinition.membersOrder ?? Object.keys(typeDefinition.members);

  if (isReadOnly) {
    return (
      <TextField
        id={field.name}
        name={field.name}
        value={field.value ?? ""}
        isReadOnly={true}
        aria-label={isListItem ? label : undefined}
        data-data-type={typeDefinition.dataType}
        data-is-list-item={isListItem}
        data-testid="widgets.RHFContentField.EnumField.root"
        className={classnames(
          cs.Field.root,
          isListItem && cs.ListItemField.root,
        )}
      >
        {!isListItem ? (
          <AnyFieldLabel
            name={field.name}
            typeDefinition={typeDefinition}
            isNullable={isNullable}
            label={label}
          />
        ) : null}
        <Input ref={fieldRef} placeholder="null" />
        <FieldError>{fieldState.error?.message}</FieldError>
      </TextField>
    );
  }

  return (
    <Select
      id={field.name}
      name={field.name}
      value={field.value ?? null}
      onChange={field.onChange}
      validationBehavior="aria"
      autoFocus={autoFocus}
      isInvalid={fieldState.invalid}
      aria-label={isListItem ? label : undefined}
      data-data-type={typeDefinition.dataType}
      data-is-list-item={isListItem}
      data-testid="widgets.RHFContentField.EnumField.root"
      className={classnames(cs.Field.root, isListItem && cs.ListItemField.root)}
    >
      {!isListItem ? (
        <AnyFieldLabel
          name={field.name}
          typeDefinition={typeDefinition}
          isNullable={isNullable}
          label={label}
        />
      ) : null}
      <SelectButton
        onClear={
          field.value !== null && !isListItem
            ? () => field.onChange(null)
            : undefined
        }
        placeholder={
          <FormattedMessage defaultMessage="null - click to select a value" />
        }
      />
      <FieldError>{fieldState.error?.message}</FieldError>
      <SelectOptions
        options={sortedMemberNames.map((memberName) => {
          const member = typeDefinition.members[memberName]!;
          return {
            id: member.value,
            label: member.value,
            description: member.description,
          };
        })}
        zoomLevel={zoomLevel}
      />
    </Select>
  );
}
